import React, { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { RequireAuth, RequireGuest } from './guards';
import { AuthRoutes } from '@xarvio/frontend-core-router/lib/AuthRoutes';
// Eager on purpose: Fast Refresh registration breaks for the remote's lazy
// landing-page chunk, and login is the dominant entry route, so shipping it in
// the routes chunk is an accepted production trade-off.
import { LoginPage } from './pages/LoginPage/LoginPage';

const RegistrationPage = lazy(() =>
  import('./pages/RegistrationPage/RegistrationPage').then((m) => ({
    default: m.RegistrationPage,
  })),
);
const AutoLoginPage = lazy(() =>
  import('./pages/AutoLoginPage/AutoLoginPage').then((m) => ({
    default: m.AutoLoginPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
// const ResetPasswordMobilePage = lazy(() =>
//   import('./pages/ResetPasswordPage/ResetPasswordMobilePage').then((m) => ({
//     default: m.ResetPasswordMobilePage,
//   })),
// );
const EmailVerifiedPage = lazy(() =>
  import('./pages/EmailVerifiedPage/EmailVerifiedPage').then((m) => ({
    default: m.EmailVerifiedPage,
  })),
);
const AcceptTermsAndConditionsPage = lazy(() =>
  import(
    './pages/AcceptTermsAndConditionsPage/AcceptTermsAndConditionsPage'
  ).then((m) => ({
    default: m.AcceptTermsAndConditionsPage,
  })),
);
const SmartSprayerLandingPage = lazy(() =>
  import('./pages/SmartSprayerLandingPage/SmartSprayerLandingPage').then(
    (m) => ({
      default: m.SmartSprayerLandingPage,
    }),
  ),
);

interface PublicAuthModuleRoutesOptions {
  defaultRedirectPath?: string;
  guestRedirectPath?: string;
}

export const getPublicAuthModuleRoutes = (
  options: PublicAuthModuleRoutesOptions = {},
): RouteObject[] => {
  const { defaultRedirectPath = AuthRoutes.LOGIN, guestRedirectPath } = options;
  return [
    {
      index: true,
      element: <Navigate to={AuthRoutes.LOGIN} replace />,
    },
    {
      path: AuthRoutes.LOGIN,
      element: (
        <RequireGuest redirectPath={guestRedirectPath}>
          <LoginPage />
        </RequireGuest>
      ),
    },
    {
      path: AuthRoutes.REGISTRATION,
      element: (
        <RequireGuest redirectPath={guestRedirectPath}>
          <RegistrationPage />
        </RequireGuest>
      ),
    },
    {
      path: AuthRoutes.SMART_SPRAYER_LANDING,
      element: <SmartSprayerLandingPage />,
    },
    {
      path: AuthRoutes.SMART_SPRAYER_REGISTRATION,
      element: (
        <RequireGuest redirectPath={guestRedirectPath}>
          <RegistrationPage isSmartSprayerRegistration />
        </RequireGuest>
      ),
    },
    {
      path: AuthRoutes.AUTO_LOGIN,
      element: <AutoLoginPage />,
    },
    {
      path: AuthRoutes.FORGOT_PASSWORD,
      element: (
        <RequireGuest redirectPath={guestRedirectPath}>
          <ForgotPasswordPage />
        </RequireGuest>
      ),
    },
    {
      path: AuthRoutes.RESET_PASSWORD,
      element: (
        <RequireGuest redirectPath={guestRedirectPath}>
          <ResetPasswordPage />
        </RequireGuest>
      ),
    },
    {
      path: AuthRoutes.EMAIL_VERIFIED,
      element: (
        <RequireAuth>
          <EmailVerifiedPage />
        </RequireAuth>
      ),
    },
    {
      path: AuthRoutes.ACCEPT_TC,
      element: <AcceptTermsAndConditionsPage />,
    },
    {
      path: '*',
      element: <Navigate to={defaultRedirectPath} replace />,
    },
  ];
};
